import { MenuItem } from '@mui/material';
import CustomSelect, { CustomSelectProps } from '../UI/CustomSelect';
import { useMemo } from 'react';

export type SortedSelectOption = {
    id: number;
    name: string;
};

export type sortFunctionType<A> = <T extends A>(a: T, b: T) => number;

export type SortedSelectProps<T extends SortedSelectOption> = {
    label: string;
    selectedValue: number;
    options: T[];
    onChange: (value: number) => void;
    sortByName?: boolean;
    sortFunction?: sortFunctionType<T>;
} & Omit<CustomSelectProps<number>, 'onChange' | 'selectedValue' | 'options' | 'label'>;

function sortByNameFunction<T extends SortedSelectOption>(a: T, b: T) {
    if (a.id === -1 || a.name === 'Alle') return -1;
    if (b.id === -1 || b.name === 'Alle') return 1;
    return a.name.localeCompare(b.name);
}

function SortedSelect<T extends SortedSelectOption>({
    label,
    selectedValue,
    options,
    onChange,
    sortFunction,
    sortByName = true,
    ...props
}: SortedSelectProps<T>) {
    const sortedOptions = useMemo(
        () => (sortByName || sortFunction ? [...options].sort(sortFunction || sortByNameFunction) : options),
        [options, sortByName, sortFunction]
    );

    return (
        <CustomSelect
            selectedValue={selectedValue}
            options={sortedOptions}
            onChange={onChange}
            label={label}
            {...props}
        >
            {sortedOptions &&
                sortedOptions.map((option) => (
                    <MenuItem value={option.id} key={option.id}>
                        <span className="notranslate">{option.name}</span>
                    </MenuItem>
                ))}
        </CustomSelect>
    );
}

export default SortedSelect;
